import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL ?? 'http://localhost:8000',
  timeout: 60000,
});

// ── Auth interceptors ──────────────────────────────────────────────────

// Request: attach JWT token from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response: redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if we're already on auth endpoints
      const url = error.config?.url || '';
      if (!url.includes('/api/auth/')) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// ── Auth API ────────────────────────────────────────────────────────────

export const authApi = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  me: () => api.get('/api/auth/me'),
  createInvite: (data) => api.post('/api/auth/invite', data || {}),
  listInvites: () => api.get('/api/auth/invites'),
  listUsers: () => api.get('/api/auth/users'),
  deleteUser: (id) => api.delete(`/api/auth/users/${id}`),
};

// ── Existing APIs ───────────────────────────────────────────────────────

export const cardApi = {
  list: (params) => api.get('/api/cards', { params }),
  get: (id) => api.get(`/api/cards/${id}`),
  update: (id, data) => api.patch(`/api/cards/${id}`, data),
  delete: (id) => api.delete(`/api/cards/${id}`),
  backfillMetadata: () => api.post('/api/cards/backfill-metadata'),
  autoLink: () => api.post('/api/cards/auto-link'),
  deleteUnlinked: () => api.delete('/api/cards/unlinked'),
};

export const scraperApi = {
  run: (sources) => api.post('/api/scraper/run', { sources }),
  status: (jobId) => api.get(`/api/scraper/status/${jobId}`),
  addByUrl: (url) => api.post('/api/scraper/add-by-url', { url }),
};

export const alertApi = {
  list: () => api.get('/api/alerts'),
  create: (data) => api.post('/api/alerts', data),
  get: (id) => api.get(`/api/alerts/${id}`),
  update: (id, data) => api.patch(`/api/alerts/${id}`, data),
  delete: (id) => api.delete(`/api/alerts/${id}`),
};

export const notificationApi = {
  list: (params) => api.get('/api/notifications', { params }),
  count: () => api.get('/api/notifications/count'),
  markRead: (id) => api.patch(`/api/notifications/${id}/read`),
  markAllRead: () => api.patch('/api/notifications/read-all'),
};

export const tcgApi = {
  populate: () => api.post('/api/tcg/populate'),
  populateStatus: (jobId) => api.get(`/api/tcg/populate/status/${jobId}`),
  reset: () => api.post('/api/tcg/reset'),
  listSets: (params) => api.get('/api/tcg/sets', { params }),
  listCards: (params) => api.get('/api/tcg/cards', { params }),
  getCard: (id) => api.get(`/api/tcg/cards/${id}`),
};

export default api;
